/**
 * SERVER-ONLY (REST) data gate for screens with no local-first mirror
 * (billing, CRM, metrics…): with `networkMode: "offlineFirst"` a query with
 * no network stays `pending`+`paused` and the skeleton would spin FOREVER.
 * This gate gives HONEST states: skeleton only while it's really loading, a
 * clear offline state when there's no network, and error with retry when the
 * server fails.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GATE ONLY CLOSES BEFORE THE FIRST DATA (fix 2026-08-14)
 * ─────────────────────────────────────────────────────────────────────────────
 * The three "no content" states (offline / error / skeleton) are valid while
 * the screen has NOT PAINTED ANYTHING YET. Once the children have mounted
 * with data, going back to any of them UNMOUNTS them, and with them goes all
 * the subtree's local state: whatever the user had typed into a form
 * disappears without warning.
 *
 * It's not hypothetical — it's the defect measured in
 * `billing-discounts.spec`: `BillingSettings` passes `activeOrgId`
 * (local-first, arrives via PowerSync ONE INSTANT AFTER the first paint) to
 * its primary query. When the org resolves, the query key changes, TanStack
 * reports `isLoading` because there's no cache for the new key, this gate
 * jumped to the skeleton and `DiscountsSection` remounted clean. The admin
 * typed "PROMO50 / 50", the form emptied itself out ~1s after the page
 * loaded, and the "Create" button no longer emitted ANY POST: the submit saw
 * `value() === ""` and bailed out on validation (`Number("") <= 0`), showing
 * an invalid-value toast. User input silently discarded.
 *
 * That's why the contract is: **the gate only closes before the first
 * data**. After that, the children stay mounted and each section resolves
 * its own freshness with its own `<Show>`/skeleton — which is exactly the
 * stale-while-revalidate behavior its consumers already expect.
 */
import {
  type Component,
  createEffect,
  createSignal,
  type JSX,
  Match,
  onCleanup,
  onMount,
  Switch,
} from "solid-js";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { NotFoundState } from "./not-found-state";
import { Skeleton } from "@fleet/ui";

interface ServerDataGateProps {
  /** The primary query has no result yet (isPending / isLoading). */
  pending: boolean;
  /** The primary query failed (isError). */
  error: boolean;
  /** Retry (refetch of the primary query). */
  refetch: () => void;
  /** Skeleton to show while loading with network (default: generic block). */
  skeleton?: JSX.Element;
  /**
   * `data-testid` for the ERROR state. Defaults to `server-error-state`,
   * which is what the existing suites probe for. Can be customized when
   * there are SEVERAL gates on the same screen (the profile mounts sessions
   * and connected accounts): without an id of its own, one section's probe
   * would pass green because of the other's error.
   */
  testId?: string;
  children: JSX.Element;
}

/** Reactive browser network signal (enough for REST screens). */
function createOnlineSignal(): () => boolean {
  const [online, setOnline] = createSignal(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  onMount(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    globalThis.addEventListener("online", up);
    globalThis.addEventListener("offline", down);
    onCleanup(() => {
      globalThis.removeEventListener("online", up);
      globalThis.removeEventListener("offline", down);
    });
  });
  return online;
}

export const ServerDataGate: Component<ServerDataGateProps> = (props) => {
  const { t } = useI18n();
  const online = createOnlineSignal();

  // Has the first data already arrived? From then on the gate won't close
  // again: a refetch `pending` (or a query key change) must NOT unmount the
  // children and wipe out whatever the user has typed.
  const [settledOnce, setSettledOnce] = createSignal(false);
  createEffect(() => {
    if (!props.pending && !props.error) {
      setSettledOnce(true);
    }
  });
  // The `||` covers the first render with data: the effect hasn't run yet,
  // but the children must already paint (so there's no extra skeleton frame).
  const showChildren = () => settledOnce() || (!props.pending && !props.error);

  return (
    <Switch>
      {/* Data already arrived: children take over and stay mounted no matter what. */}
      <Match when={showChildren()}>{props.children}</Match>
      {/* No network and no data: honest offline state (not an eternal spinner). */}
      <Match when={(props.pending || props.error) && !online()}>
        <div data-testid="server-offline-state">
          <NotFoundState
            variant="offline"
            title={t(msg`This screen needs a connection`)}
            description={t(
              msg`This data lives on the server and isn't available offline. Reconnect to see it.`,
            )}
            secondaryAction={{
              label: t(msg`Retry`),
              onClick: () => {
                props.refetch();
              },
            }}
          />
        </div>
      </Match>
      {/* Network is up but the server failed: error + retry. */}
      <Match when={props.error}>
        <div data-testid={props.testId ?? "server-error-state"}>
          <NotFoundState
            variant="offline"
            title={t(msg`Could not load this data`)}
            description={t(msg`The server didn't respond. Try again in a moment.`)}
            primaryAction={{
              label: t(msg`Retry`),
              onClick: () => {
                props.refetch();
              },
            }}
          />
        </div>
      </Match>
      {/* Really loading (with network) and still no data at all. */}
      <Match when={props.pending}>
        {props.skeleton ?? <Skeleton class="h-40 w-full rounded-2xl" />}
      </Match>
      <Match when>{props.children}</Match>
    </Switch>
  );
};
